import { gql, useQuery } from '@apollo/client'
import * as _ from 'lodash'
import moment from 'moment'
import { useMemo } from 'react'

import { useDateFilter } from '../../../dateFilter'
import { useGroupFilter } from '../../../groupFilter'
import { useVariables } from '../../../variables'
import { IApiType } from '../../types'

type IMetricDataType = Record<
  string,
  {
    name?: string
    unit: 'CENT' | 'PERCENTAGE' | 'DOLLAR' | 'COUNT'
    value: number
  }
>

type ListItemMetricValue = {
  startDate: string
  endDate: string
  itemName: string
  displayParentCategoryName: string
  metricData: IMetricDataType
  metricSummaryData: IMetricDataType
}

type IDataType = {
  listItemMetricValues: {
    nodes: ListItemMetricValue[]
  }
}

const itemQuery = gql`
  query listItemMetricValuesV2(
    $iStartDate: Date!
    $iEndDate: Date!
    $iFilter: JSON!
  ) {
    listItemMetricValues: listItemMetricValuesV2(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iQueryType: "ITEM"
      iParams: $iFilter
    ) {
      nodes {
        startDate
        endDate
        metricData
        metricSummaryData
      }
    }
  }
`

export const itemTrendConfigs = {
  itemName: 'string',
  displayParentCategoryName: 'string',
  quantitySold: 'number',
  totalSales: 'price',
  dateRangeLabel: 'string',
} as const

const useItemTrend = (): IApiType => {
  const { startDate, endDate } = useDateFilter()
  const { groupFilter, hasGroupBy } = useGroupFilter()
  const { variables } = useVariables()
  const selectedItemOrCategory = useMemo(
    () => variables.items?.value,
    [variables],
  )

  const itemCategoryIds = parseInt(selectedItemOrCategory?.[0]?.[0]) || null
  const itemIds = parseInt(selectedItemOrCategory?.[0]?.[1]) || null

  const { data, loading } = useQuery<IDataType>(itemQuery, {
    variables: {
      iStartDate: startDate,
      iEndDate: endDate,
      iQueryType: 'ITEM',
      iFilter: {
        metrics: ['quantity_sold', 'total_sales'],
        item_ids: itemIds ? [itemIds] : [],
        item_category_ids:
          _.isNil(itemIds) && itemCategoryIds ? [itemCategoryIds] : [],
        item_codes: itemIds || itemCategoryIds ? [] : ['ALL ITEMS'],
        group_by: ['LOCATION', 'BUSINESS_WEEK'],
        ...(hasGroupBy
          ? {
              location_group_ids: groupFilter?.ids,
            }
          : {
              location_ids: groupFilter?.ids,
            }),
      },
    },
    skip: !startDate || !endDate || !groupFilter,
  })

  return {
    data: useMemo(() => {
      if (!data?.listItemMetricValues) return null

      return data.listItemMetricValues.nodes
        .filter(({ metricSummaryData }) => metricSummaryData)
        .map(({ startDate, endDate, metricSummaryData }) => ({
          ..._.mapValues(
            _.mapKeys(metricSummaryData, (_value, key) => _.camelCase(key)),
            (value) => {
              switch (value.unit) {
                case 'PERCENTAGE':
                case 'DOLLAR':
                  return _.isNil(value.value) ? null : value.value * 100
                default:
                  return value.value
              }
            },
          ),
          dateRangeLabel: `${moment
            .utc(startDate as string)
            .format('M/D/YY')} - ${moment
            .utc(endDate as string)
            .format('M/D/YY')}`,
          id: startDate,
          parentId: 'root',
        }))
    }, [data]),
    loading,
  }
}

export default useItemTrend
